/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useContext, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import { connect, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
    object, string, shape, arrayOf, number, bool,
} from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useLazyQuery } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import mbpLogger from '@module-logger/mbp';
import findProductByPartNumber from '../../../../../../gql/queries/findProductByPartNumber';
import GraphqlPortalLoader from '../../../../GraphqlCommonComponents/GraphqlPortalLoader';
import FoodQuickViewContent from './FoodQuickViewContent';
import { getAddToCartLoaderStatus } from '../../../../../../../state/ducks/Common/Common-Selectors';
import SiteLoader from '../../../../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import MiniCart from '../../../../../Checkout/containers/CheckoutFood/components/Partials/MiniCart/MiniCart';
import { CrossMerch } from '../../../../CrossMerchContainer/CrossMerchContainer';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { clearAddToCartError } from '../../../../../../../state/ducks/AddToCart/AddToCart-Actions';

const styles = (theme) => ({
    rootModal: {
        maxWidth: '800px',
        [theme.breakpoints.up(1279)]: {
            maxWidth: 950,
        },
    },
    quickViewModal: {
        padding: '0px 0px',
        marginTop: '-8px',
        backgroundColor: 'transparent',
        border: 0,
        cursor: 'pointer',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        '&:hover': {
            color: 'blue',
        },
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    modal: {
        overflowY: 'hidden',
    },
    quickViewText: {
        fontSize: '14px',
        textDecoration: 'underline',
    },
    productImageUI: {
        padding: '0px 10px 0px 10px',
        minWidth: '50%',
    },
    quickUpsellName: {
        color: theme.palette.cms?.pdpQuickView,
        fontFamily: theme.typography.fontFamily,
        padding: '0 5px',
        width: '82%',
        float: 'left',
        fontWeight: '700',
        fontStyle: 'normal',
        fontSize: '18px',
    },
    quickUpsellPrice: {
        paddingLeft: '29%',
        color: theme.palette.cms?.pdpQuickView,
        fontFamily: theme.typography.fontFamily,
        fontWeight: '700',
        fontStyle: 'normal',
        fontSize: '18px',
    },
    cancelView: {
        left: '0px',
        top: '0px',
        width: '107px',
        height: '38px',
        background: 'inherit',
        textTransform: 'capitalize',
        backgroundColor: theme.palette.primary?.globalBannerContainerBackground,
        border: `2px solid ${theme.palette.main || '#2f2f2f'}`,
        borderRadius: '20px',
        boxShadow: 'none',
        fontWeight: '700',
        fontStyle: 'normal',
        fontSize: '16px',
        color: theme.palette.main,
        '&:hover': {
            backgroundColor: theme.palette.cms?.icon,
        },
    },
    selectView: {
        borderWidth: '0px',
        textTransform: 'capitalize',
        left: '0px',
        top: '0px',
        width: '156px',
        height: '38px',
        background: 'inherit',
        backgroundColor: theme.palette.secondary?.main,
        border: 'none',
        borderRadius: '21px',
        fontWeight: '700',
        fontStyle: 'normal',
        fontSize: '16px',
        color: theme.palette.globalBannerContainerBackground,
        '&:hover': {
            backgroundColor: theme.palette.cms?.bgSelectColor,
        },
    },
    textArea: {
        width: '100%',
        height: 300,
        resize: 'none',
        overflowY: 'scroll',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '0px',
        borderColor: theme.palette.cms?.skuAvailableToShipTextColor,
        marginTop: '10px',
    },
    rootTextArea: {
        padding: '15px 14px 0px 0px',
    },
    contentStyle: {
        padding: '2px 0px 2px 13px',
        fontWeight: '400',
        fontSize: '14px',
        color: theme.palette.text?.hint,
    },
    paperWidthSm: {
        maxWidth: '800px',
        maxHeight: 'fit-content',
        width: '100%',
        msOverflowStyle: 'none',
        padding: '1em 1em 1.5em',
        [theme.breakpoints.up(1279)]: {
            maxWidth: 950,
        },
    },
    dialogActions: {
        bottom: '10px',
        right: '10px',
        [theme.breakpoints.down(1280)]: {
            position: 'absolute',
        },
    },
    clickMore: {
        margin: '7% 0% 0% 90%',
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    contentTitle: {
        fontWeight: '700',
        padding: '3px 0px 3px 3px',
    },
    longDescMarkDown: {
        margin: '-15px 0px 0px -11px',
    },
    altImagesContainer: {
        [theme.breakpoints.up(1279)]: {
            maxHeight: 425,
            overflowY: 'auto',
            flexWrap: 'wrap',
        },
        flexWrap: 'nowrap',
    },
    closeIcon: {
        height: 35,
        textAlign: 'right',
    },
});

const GraphqlFoodQuickViewModal = ({
    classes,
    location,
    productUrl,
    quickViewData,
    product,
    brand,
    history,
    selectedSku,
    showLoadingMessage,
    onClickTracking,
}) => {
    if (!quickViewData) {
        return null;
    }
    const dispatch = useDispatch();
    const { featureFlags = {} } = useContext(CrossMerch);
    const [miniCartModalOpen, setMiniCartModalOpen] = React.useState(false);
    const domain = brand?.domain || null;
    const selectId = quickViewData.id || '';
    const selectedName = quickViewData.name || '';
    const partNumber = quickViewData.partNumber || '';
    const shortDescription = quickViewData.shortDescription || '';
    const image = quickViewData.image || '';
    const productContentSku = quickViewData.productSkus || [];
    const quickViewLongDesc = productContentSku?.[0]?.longDescription || quickViewData.longDescription || '';
    const hasWine = productContentSku?.[0]?.ageVerifyFlag === 'true';
    const [open, setOpen] = React.useState(false);

    const FIND_PDP_BY_PART = findProductByPartNumber(domain, selectId, 'id,seo{url}');
    const [loadPDP, { errorPDP, loading, data }] = useLazyQuery(FIND_PDP_BY_PART);

    if (loading) {
        return <GraphqlPortalLoader />;
    }
    if (data) {
        const productURL = data?.findProductByPartNumber?.seo?.url || null;
        if (productURL) {
            history.push(productURL);
        }
    }
    if (errorPDP) {
        mbpLogger.logError({ appName: process.env.npm_package_name, message: `error prefetching PDP  ${errorPDP}` });
    }

    useEffect(() => {
        dispatch(clearAddToCartError());
    }, [open]);

    const handleClickOpen = (event) => {
        event.preventDefault();
        if (onClickTracking) {
            dispatch(trackEvent(onClickTracking));
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const selectQuickSKU = () => {
        setOpen(false);
        loadPDP();
    };
    const renderMiniCart = () => {
        if (featureFlags['is-mini-cart-enabled'] || featureFlags['is-mini-cart-v2-enabled']) {
            return (
                <>
                    {(showLoadingMessage && !miniCartModalOpen)
                    && (
                        <SiteLoader />
                    )}
                    <MiniCart
                        handleMiniCartModalClick={() => {
                            setMiniCartModalOpen(!miniCartModalOpen);
                        }}
                        minicartModalOpen={miniCartModalOpen}
                        product={product}
                        presentation_family="food"
                        skuData={productContentSku}
                        isQuickView
                    />
                </>
            );
        }
        return null;
    };
    const showOldView = false;  // if you set value true then old view show, otherwise is showing new
    return (
        <Grid>
            {renderMiniCart()}
            <button type="button" onClick={handleClickOpen} className={classes.quickViewModal}>
                <span className={classes.quickViewText}>Quick View</span>
            </button>
            <div className={classes.rootModal}>
                <Dialog
                    data-testid="quick-view-modal" // didn't worked with querySelector have to use test id
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    classes={{
                        paperWidthSm: classes.paperWidthSm,
                    }}
                >
                    <Grid container className={classes.modal}>
                        <Grid className={classes.closeIcon} item sm={12} lg={12}>
                            <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <FoodQuickViewContent
                            quickViewData={quickViewData}
                            product={product}
                            domain={brand?.domain}
                            selectId={selectId}
                            selectedName={selectedName}
                            partNumber={partNumber}
                            shortDescription={shortDescription}
                            image={image}
                            productContentSku={selectedSku}
                            quickViewLongDesc={quickViewLongDesc}
                            productUrl={productUrl}
                            location={location}
                            hasWine={hasWine}
                            addToCartHide={!showOldView}
                            handleMiniCartModalClick={() => {
                                setOpen(false);
                                setMiniCartModalOpen(!miniCartModalOpen);
                            }}
                            enableMiniCart={featureFlags['is-mini-cart-enabled'] || featureFlags['is-mini-cart-v2-enabled']}
                        />
                        {showOldView && (
                            <Grid item sm={12} lg={12} className={classes.dialogActions}>
                                <DialogActions>
                                    <Button id="close-quick-view" onClick={handleClose} className={classes.cancelView}>
                                        Close
                                    </Button>
                                    <Button id="select-quick-view" onClick={selectQuickSKU} className={classes.selectView}>
                                        Select
                                    </Button>
                                </DialogActions>
                            </Grid>
                        )}

                    </Grid>
                </Dialog>
            </div>
        </Grid>
    );
};

GraphqlFoodQuickViewModal.propTypes = {
    classes: object.isRequired,
    location: shape({
        search: string,
    }),
    product: shape({
        name: string.isRequired,
        partNumber: string.isRequired,
        shortDescription: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }).isRequired,
        availability: shape({
            deliveryMessage: string.isRequired,
            productDeliveryType: string.isRequired,
        }).isRequired,
    }).isRequired,
    quickViewData: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    productUrl: string.isRequired,
    brand: shape({
        domain: string,
    }),
    history: shape({
        location: shape({
            pathname: string,
        }),
    }).isRequired,
    showLoadingMessage: bool,
    onClickTracking: shape({
        eventCategory: string,
        eventAction: string,
        eventLabel: string,
    }),
};
GraphqlFoodQuickViewModal.defaultProps = {
    location: {
        search: '',
    },
    brand: {
        domain: '',
    },
    showLoadingMessage: false,
    onClickTracking: null,
};
const mapStateToProps = (state) => ({
    showLoadingMessage: getAddToCartLoaderStatus(state),
});
const enhance = compose(
    withStyles(styles),
    connect(mapStateToProps, null),
    withRouter,
);

export default enhance(GraphqlFoodQuickViewModal);
